import React, { useState, useEffect } from 'react';
import { Layout, Menu, Card, Typography, Button, Space, Alert, Spin, Row, Col, Tag } from 'antd';
import {
  DashboardOutlined,
  HistoryOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  UserOutlined,
  CalendarOutlined,
  ExclamationCircleOutlined,
  BookOutlined,
  MedicineBoxOutlined,
  SettingOutlined,
  ExperimentOutlined,
  EyeOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import StaffPreviewHealthOverview from '../components/StaffPreviewHealthOverview';
import StaffPreviewTimeline from '../components/StaffPreviewTimeline';
import StaffPreviewEducation from '../components/StaffPreviewEducation';

const { Header, Content, Sider } = Layout;
const { Title, Text } = Typography;

const StaffPortalPreview = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [selectedKey, setSelectedKey] = useState('overview');
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch patient data for preview context
  useEffect(() => {
    const fetchPatientData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/patients/${patientId}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        setPatient(response.data.data.patient);
        setError(null);
      } catch (err) {
        console.error('Error fetching patient data:', err);
        setError('Failed to load patient data for preview');
      } finally {
        setLoading(false);
      }
    };

    if (patientId && token) {
      fetchPatientData();
    }
  }, [patientId, token]);

  const handleBackToPatient = () => {
    navigate(`/patients/${patientId}`);
  };

  const menuItems = [
    {
      key: 'overview',
      icon: <DashboardOutlined />,
      label: 'Health Overview'
    },
    {
      key: 'timeline',
      icon: <CalendarOutlined />,
      label: 'Timeline'
    },
    {
      key: 'education',
      icon: <BookOutlined />,
      label: 'Education'
    }
  ];

  const renderContent = () => {
    switch (selectedKey) {
      case 'overview':
        return <StaffPreviewHealthOverview patientId={patientId} />;
      case 'timeline':
        return <StaffPreviewTimeline patientId={patientId} />;
      case 'education':
        return <StaffPreviewEducation patientId={patientId} />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px' }}>
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          action={
            <Button size="small" onClick={handleBackToPatient}>
              Back to Patient
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <Layout style={{ minHeight: '100vh', background: '#f0f2f5' }}>
      {/* Preview Header with Patient Info */}
      <Header style={{ 
        background: '#1890ff', 
        padding: '0 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Space>
          <Button 
            type="text" 
            icon={<ArrowLeftOutlined />} 
            onClick={handleBackToPatient}
            style={{ color: 'white' }}
          >
            Back to Patient Chart
          </Button>
          <div style={{ color: 'white', marginLeft: '20px' }}>
            <Title level={4} style={{ color: 'white', margin: 0 }}>
              <EyeOutlined style={{ marginRight: '8px' }} />
              Patient Portal Preview
            </Title>
            <Text style={{ color: 'rgba(255,255,255,0.8)' }}>
              {patient ? `${patient.firstName} ${patient.lastName}` : 'Loading...'}
            </Text>
          </div>
        </Space>
        <Space>
          <Tag color="orange" style={{ margin: 0 }}>
            <EyeOutlined style={{ marginRight: '4px' }} />
            STAFF PREVIEW MODE
          </Tag>
          <Text style={{ color: 'rgba(255,255,255,0.8)' }}>
            Viewing as: {user?.email}
          </Text>
        </Space>
      </Header>

      <Layout>
        {/* Sidebar Navigation */}
        <Sider 
          width={250} 
          style={{ 
            background: '#fff',
            boxShadow: '2px 0 8px rgba(0,0,0,0.1)'
          }}
        >
          <div style={{ padding: '20px 16px' }}>
            <Title level={5} style={{ margin: '0 0 16px 0', color: '#1890ff' }}>
              Portal Sections
            </Title>
            <Menu
              mode="inline"
              selectedKeys={[selectedKey]}
              onClick={({ key }) => setSelectedKey(key)}
              style={{ border: 'none' }}
              items={menuItems}
            />
          </div>
        </Sider>

        {/* Main Content */}
        <Layout style={{ padding: '0' }}>
          <Content style={{ 
            margin: '0',
            background: '#fff',
            minHeight: 'calc(100vh - 64px)',
            overflow: 'auto'
          }}>
            {/* Preview Notice */}
            <div style={{ 
              background: 'linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%)',
              padding: '12px 24px',
              borderBottom: '1px solid #e8e8e8'
            }}>
              <Space>
                <ExclamationCircleOutlined style={{ color: '#856404' }} />
                <Text style={{ color: '#856404', fontWeight: '500' }}>
                  You are viewing the patient portal interface as it appears to the patient. 
                  This preview uses your clinical authentication.
                </Text>
              </Space>
            </div>

            {/* Portal Content */}
            <div style={{ padding: '24px' }}>
              {renderContent()}
            </div>
          </Content>
        </Layout>
      </Layout>
    </Layout>
  );
};

export default StaffPortalPreview;
